import * as React from "react";
import clsx from "clsx";
import { Check, X } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { Switch } from "../../../ui/components/Switch";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  fetchSmartLinkProductComparison,
  setSmartLinkProductEnabled,
  syncSmartLinkProductFromPro,
  syncSmartLinkProductFromSmart,
} from "../../lib/api/smart-link";
import type { SmartLinkProductComparison } from "../../lib/api/smart-link";
import * as s from "./SmartLinkClaimCompareModal.css";

export interface SmartLinkProductCompareModalProps {
  productId: string;
  productName: string;
  linkEnabled: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  onSynced?: () => void;
}

const EDITABLE_IN_PRO = new Set(["name", "address", "addressSecondary", "city", "state", "zip", "country", "timezone", "smartSystemFirmwareId"]);

function formatValue(t: (key: string) => string, value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") {
    return value ? t("pro.system.smartLink.compare.yes") : t("pro.system.smartLink.compare.no");
  }
  if (Array.isArray(value)) return value.length > 0 ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function SmartLinkProductCompareModal({
  productId,
  productName,
  linkEnabled: initialLinkEnabled,
  open,
  setOpen,
  onSynced,
}: SmartLinkProductCompareModalProps) {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [comparison, setComparison] = React.useState<SmartLinkProductComparison | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<"pro" | "smart" | "toggle" | null>(null);
  const [linkEnabled, setLinkEnabled] = React.useState(initialLinkEnabled);

  React.useEffect(() => setLinkEnabled(initialLinkEnabled), [productId, initialLinkEnabled]);

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setComparison(await fetchSmartLinkProductComparison(token, productId));
      onSynced?.();
    } catch (err: any) {
      setError(err?.message || t("pro.system.smartLink.compare.error.load"));
    } finally {
      setLoading(false);
    }
  }, [token, productId, t, onSynced]);

  React.useEffect(() => {
    if (!open || !linkEnabled) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId, linkEnabled]);

  const run = async (kind: "pro" | "smart") => {
    if (!token || busy) return;
    setBusy(kind);
    setError(null);
    try {
      const next =
        kind === "pro"
          ? await syncSmartLinkProductFromPro(token, productId)
          : await syncSmartLinkProductFromSmart(token, productId);
      setComparison(next);
      onSynced?.();
    } catch (err: any) {
      setError(
        err?.message ||
          t(kind === "pro" ? "pro.system.smartLink.compare.syncFromProError" : "pro.system.smartLink.compare.syncFromSmartError"),
      );
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (enabled: boolean) => {
    if (!token || busy) return;
    setBusy("toggle");
    setError(null);
    try {
      const res = await setSmartLinkProductEnabled(token, productId, enabled);
      setLinkEnabled(res.linkEnabled);
      onSynced?.();
    } catch (err: any) {
      setError(err?.message || t("pro.system.smartLink.manage.toggleError"));
    } finally {
      setBusy(null);
    }
  };

  const mismatched = comparison?.fields.filter((f) => !f.matches) ?? [];
  const allSynced = comparison ? mismatched.length === 0 : null;
  const proEditableDrift = mismatched.some((f) => EDITABLE_IN_PRO.has(f.field));

  return (
    <Modal open={open} setOpen={setOpen} minWidth="480px" maxWidth="680px" maxHeight="75vh" closeOnClickOff>
      <Modal.Header>
        <Text level="h2" weight="bold" className={s.title}>
          {t("pro.system.smartLink.products.manage.title", { name: productName })}
        </Text>
        {linkEnabled && allSynced !== null && (
          <div className={s.statusCallout}>
            <Callout variant={allSynced ? "success" : "error"} centered icon={allSynced ? <Check size={14} /> : <X size={14} />}>
              {t(allSynced ? "pro.system.smartLink.compare.status.synced" : "pro.system.smartLink.compare.status.outOfSync")}
            </Callout>
          </div>
        )}
      </Modal.Header>

      <Modal.Body>
        <div className={s.bodyStack}>
          <div className={s.section}>
            <h3 className={s.sectionTitle}>{t("pro.system.smartLink.manage.settingsSection")}</h3>
            <Switch
              label={t("pro.system.smartLink.manage.syncEnabled")}
              checked={linkEnabled}
              disabled={busy === "toggle"}
              onCheckedChange={(v) => void toggle(v)}
            />
          </div>

          <div className={s.section}>
            <h3 className={s.sectionTitle}>{t("pro.system.smartLink.compare.title")}</h3>
            {!linkEnabled && (
              <div className={s.banner}>
                <Callout variant="info">{t("pro.system.smartLink.manage.compareDisabled")}</Callout>
              </div>
            )}
            {linkEnabled && loading && !comparison && <AuthTransitionScreen variant="compact" />}
            {error && (
              <div className={s.banner}>
                <Callout variant="error">{error}</Callout>
              </div>
            )}
            {linkEnabled && comparison?.smartError && (
              <div className={s.banner}>
                <Callout variant="error">
                  {t("pro.system.smartLink.compare.smartUnavailable")} ({comparison.smartError})
                </Callout>
              </div>
            )}
            {linkEnabled && comparison && comparison.fields.length > 0 && (
              <>
                <div className={s.columnHeaders}>
                  <p className={s.columnHeaderLabel}>{t("pro.system.smartLink.compare.col.lionEnergy")}</p>
                  <span />
                  <p className={s.columnHeaderLabel}>{t("pro.system.smartLink.compare.col.lionSmart")}</p>
                </div>
                <div className={s.rowList}>
                  {[...mismatched, ...comparison.fields.filter((f) => f.matches)].map((f) => (
                    <div key={f.field}>
                      <p className={s.fieldLabel}>{t(`pro.system.smartLink.products.field.${f.field}`)}</p>
                      <div className={s.compareGrid}>
                        <div className={clsx(s.valueCard, !f.matches && s.valueCardMismatch)}>
                          <p className={s.valueText}>{formatValue(t, f.proValue)}</p>
                        </div>
                        <span
                          className={clsx(s.matchIcon, f.matches ? s.matchIconMatch : s.matchIconMismatch)}
                          role="img"
                          aria-label={t(f.matches ? "pro.system.smartLink.compare.matches" : "pro.system.smartLink.compare.mismatch")}
                        >
                          {f.matches ? <Check size={16} strokeWidth={2.5} /> : <X size={16} strokeWidth={2.5} />}
                        </span>
                        <div className={clsx(s.valueCard, !f.matches && s.valueCardMismatch)}>
                          <p className={s.valueText}>{formatValue(t, f.smartValue)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        {linkEnabled && allSynced === false && (
          <>
            {proEditableDrift && (
              <Button
                type="button"
                color="neutral"
                variant="outline"
                loading={busy === "pro"}
                disabled={busy !== null}
                onClick={() => void run("pro")}
              >
                {t("pro.system.smartLink.compare.syncFromPro")}
              </Button>
            )}
            <Button
              type="button"
              color="neutral"
              variant="outline"
              loading={busy === "smart"}
              disabled={busy !== null}
              onClick={() => void run("smart")}
            >
              {t("pro.system.smartLink.compare.syncFromSmart")}
            </Button>
          </>
        )}
        <Button type="button" color="primary" onClick={() => setOpen(false)}>
          {t("shared.common.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
